// Contains reusable rxjs pipes for the parent -> child report iframe data flow
/* eslint-disable */
import { diff } from 'diffj';
import { Operation } from 'diffj/dist/lib/jsonPatch';
import { combineLatest, Observable, pipe } from 'rxjs';
import { bufferTime, catchError, filter, first, map, switchMap, tap } from 'rxjs/operators';

import { EmbedReportState, EmitMessages, Messages } from '@mode/reports/sandbox/contract';
import { SelectedReportState } from '@mode/legacy/types-app-state';

import { BugsnagHelpers } from './bugsnag-helpers';

/**
 * Waits for the ready message from the child iframe
 */
export function waitReady() {
  return pipe(filter((f: Messages) => f.type === EmitMessages.MessageTypes.Ready));
}

/**
 * Gets the report layout html and theme
 * @param store
 */
export function getLayout(layout$: Observable<string>, theme$: Observable<SelectedReportState['theme']>) {
  return pipe(
    switchMap(() =>
      // Get the first non-empty layout and theme
      combineLatest(layout$, theme$).pipe(
        filter(([layout]) => layout !== ''),
        first(),
        map(([layout, theme]) => ({ layout, theme }))
      )
    )
  );
}

/**
 * Subscribes to the embed state and begins generating diffs.
 * @param embedState$
 */
export function generatePatch(embedState$: Observable<EmbedReportState.SerializedReportState>) {
  let prevState: Partial<EmbedReportState.SerializedReportState> = {};

  return pipe(
    tap(() => {
      prevState = {};
    }),
    switchMap(() => embedState$),
    filter((next) => Object.keys(next).length > 0),
    map((next) => {
      const delta = diff(prevState, next, { allowShallow: true, invertible: false });
      prevState = next;
      return delta;
    }),
    filter((delta) => delta.length > 0)
  );
}

/**
 * Buffer the patches to avoid spamming the RPC channel
 */
export function bufferPatches() {
  return pipe(
    bufferTime<Operation[]>(200), // TODO: optimize this so it doesn't fire every 200ms
    filter((buffer) => buffer.length > 0),
    // Flatten the buffer in order
    map((buffer) => [].concat.apply([], buffer) as Operation[])
  );
}

/**
 * Reports any error in the iframe observable to bugsnag
 * @param url url of the iframe
 */
export function reportError(url: string) {
  return pipe<Observable<Operation[]>, Observable<Operation[]>>(
    catchError((err, o) => {
      BugsnagHelpers.notifySafely(err, (event) => {
        event.addMetadata('iframe', {
          src: url,
        });
      });

      return o;
    })
  );
}
